// The one application form in the website's styling (docs/plans/lettings-one-form.md, phase 4) — the same state
// (lib/applicationForm.ts ApplicationState) and the same rules (fieldErrors) as the admin kit's ApplicationFields:
//
//   SiteApplicationSteps  the application in the site kit (2 Nov 2026: the website itself only books a viewing —
//                         pages/site/Viewing.tsx — and /rent/:slug/apply redirects there; the steps are kept for applying
//                         after the viewing, docs/plans/viewing-first-onboarding.md phase 4) — in four steps: About you,
//                         Where you've lived (with Right to Rent and the documents), Money (with the previous landlord),
//                         The move (with the optional ticks and the three consents). Same stars, same messages
//                         (applicant.schemas.ts websiteFormSchema / websiteViewingFormSchema on the server). The steps own
//                         the form element and the buttons; the page owns the state and the POST.
//   SiteApplicationForm   one page of fields, no headings, for an "Ask to refill" being answered (/apply/<token>,
//                         `asked`): only what Lotus Living asked for is required (refillErrors), the consents already given
//                         are not asked again unless they were asked for.
//
// Each field's problem shows under its box once the person has been in it (or after a submit attempt, `tried`), and
// `siteFormErrors` gives the page the same problems.
import { createContext, useContext, useState, type ReactNode, type RefObject } from 'react';
import { ArrowLeft, ChevronDown, FileText, Plus, Upload as UploadIcon, X } from 'lucide-react';
import { PillButton } from './Pill';
import { formatRent } from '../../pages/site/ListingCard';
import {
  EMPLOYMENT_STATUSES, VIEWING_HOURS, ADDRESS_HISTORY_MONTHS, affordability, emptyAddress, refillAsks, isWeekday,
  earliestMoveIn, freeFromLine, minUnderAdvertMinor, monthsCovered, nameOf, nextWeekday, slotLabel, todayIso, FORM_STEPS, type AddressLine, type ApplicationState, type FormMode,
} from '../../lib/applicationForm';
import { siteFormErrors, useFormSteps, needsRtrDocument, docFileError, type FormDocs } from '../../lib/applicationSteps';
import { PhoneInput } from '../ui/PhoneInput';
import { NationalitySelect } from '../ui/NationalitySelect';
import { needsShareCode, findNationality } from '../../lib/nationalities';
import { fileToUpload, type Upload } from '../../lib/reservations';
import { formatPhone } from '../../lib/phone';

const pounds = (minor: number) => `£${(minor / 100).toLocaleString('en-GB')}`;
const minorOf = (v: string) => Math.round(Number(String(v).replace(/[£,\s]/g, '')) * 100) || 0;

// The documents' rules, siteFormErrors and the step gating live in lib/applicationSteps.ts — shared with the admin kit's
// stepped form (components/lettings/ApplicationFields.tsx ApplicationSteps). Re-exported for the site's pages.
export { formDocErrors, formDocsPayload, needsRtrDocument, siteFormErrors, type FormDocs } from '../../lib/applicationSteps';

export function SiteApplicationForm({ s, set, advert, asked, tried, docs, setDocs }: {
  s: ApplicationState; set: (p: Partial<ApplicationState>) => void; advert: number;
  /** A new application or viewing request: the passport and right to rent document, uploaded with the form. */
  docs?: FormDocs; setDocs?: (d: FormDocs) => void;
  /** An "Ask to refill" being answered: the fields Lotus Living asked for (refill.fields). */
  asked?: string[] | null;
  /** The page tried to submit: every problem shows. */
  tried: boolean;
}) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const errs = siteFormErrors(s, advert, { asked, docs });
  const show = (k: string) => (tried || touched[k] ? errs[k] : undefined);
  const touch = (k: string) => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  // Mode apply: every starred field is required; answering a refill, only what was asked for.
  const need = (k: string) => (asked ? refillAsks(asked, k) : true);
  return (
    <ErrCtx.Provider value={{ show, touch, docs, setDocs }}>
      <ApplyFields s={s} set={set} advert={advert} need={need} asked={asked ?? null} />
    </ErrCtx.Provider>
  );
}

// ── Mode apply: the website's application, field for field as before ────────────────────────────────────────────────

function ApplyFields({ s, set, advert, need, asked }: { s: ApplicationState; set: (p: Partial<ApplicationState>) => void; advert: number; need: (k: string) => boolean; asked: string[] | null }) {
  const { show } = useContext(ErrCtx);
  const offered = minorOf(s.offeredRent) || advert;
  const a = affordability(s, offered);
  const incomeNeeded = need('annualIncome') && s.employmentStatus !== 'STUDENT' && s.employmentStatus !== 'RETIRED';
  const covered = monthsCovered(s);
  // Answering a refill: the consents were given with the application; they are asked again only if Lotus Living asked.
  const consents = !asked || asked.includes('consent');
  return (
    <>
      {/* You, and anyone moving in with you right under your details. */}
      <Group first>
        <Two>
          <F label="First name" required name="firstName"><input required autoComplete="given-name" value={s.firstName} onChange={(e) => set({ firstName: e.target.value })} className="site-input" /></F>
          <F label="Last name" required name="lastName"><input required autoComplete="family-name" value={s.lastName} onChange={(e) => set({ lastName: e.target.value })} className="site-input" /></F>
          <F label="Date of birth" required={need('dateOfBirth')} name="dateOfBirth"><input required={need('dateOfBirth')} type="date" autoComplete="bday" max={todayIso()} value={s.dateOfBirth} onChange={(e) => set({ dateOfBirth: e.target.value })} className="site-input" /></F>
          <F label="Email" required={need('email')} name="email"><input required={need('email')} type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} value={s.email} onChange={(e) => set({ email: e.target.value })} className="site-input" /></F>
          <F label="Mobile" required={need('mobile')} name="mobile"><PhoneInput kit="site" required={need('mobile')} value={s.mobile} onChange={(mobile) => set({ mobile })} /></F>
        </Two>
        <Members s={s} set={set} />
      </Group>

      <Group>
        <AddressRow a={s.current} onChange={(current) => set({ current })} current required={need('currentAddress')} />
        {s.previous.map((p, i) => <AddressRow key={i} a={p} onChange={(x) => set({ previous: s.previous.map((y, j) => (j === i ? x : y)) })} onRemove={() => set({ previous: s.previous.filter((_, j) => j !== i) })} />)}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <p className={`text-[13px] ${show('addressHistory') ? 'text-[var(--status-critical)]' : covered >= ADDRESS_HISTORY_MONTHS ? 'text-bronze-700' : 'text-ink-500'}`} data-invalid={show('addressHistory') ? '' : undefined}>{Math.floor(covered / 12)}y {covered % 12}m of the 3 years needed{show('addressHistory') ? ` — ${show('addressHistory')}` : ''}</p>
          <AddLink onClick={() => set({ previous: [...s.previous, emptyAddress()] })}>Previous address</AddLink>
        </div>
      </Group>

      <Group>
        <Two>
          <F label="Move-in date" required name="moveInDate" hint={freeFromLine(s, 'Available')}><input required type="date" min={earliestMoveIn(s)} value={s.moveInDate} onChange={(e) => set({ moveInDate: e.target.value })} className="site-input" /></F>
          <F label="Rent you offer a month (£)" required={need('offeredRent')} name="offeredRent"><input required={need('offeredRent')} inputMode="decimal" min={advert ? minUnderAdvertMinor(advert) / 100 : undefined} max={advert ? advert / 100 : undefined} value={s.offeredRent} onChange={(e) => set({ offeredRent: e.target.value })} className="site-input" /></F>
        </Two>
      </Group>

      <Group>
        <Two>
          <F label="Employment" required={need('employmentStatus')}><select required value={s.employmentStatus} onChange={(e) => set({ employmentStatus: e.target.value })} className="site-select">{EMPLOYMENT_STATUSES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}</select></F>
          <F label="Employer" name="employer" required={!!asked && need('employer')}><input autoComplete="organization" value={s.employer} onChange={(e) => set({ employer: e.target.value })} className="site-input" /></F>
          <F label="Job title" name="jobTitle" required={!!asked && need('jobTitle')}><input autoComplete="organization-title" value={s.jobTitle} onChange={(e) => set({ jobTitle: e.target.value })} className="site-input" /></F>
          <F label="Start date" name="startDate" required={!!asked && need('startDate')}><input type="date" value={s.startDate} onChange={(e) => set({ startDate: e.target.value })} className="site-input" /></F>
          <F label="Annual income before tax (£)" required={incomeNeeded} name="annualIncome"><input required={incomeNeeded} inputMode="decimal" value={s.annualIncome} onChange={(e) => set({ annualIncome: e.target.value })} className="site-input" /></F>
          <F label="Other income (£)" name="otherIncome" required={!!asked && need('otherIncome')}><input inputMode="decimal" value={s.otherIncome} onChange={(e) => set({ otherIncome: e.target.value })} placeholder="Including benefits" className="site-input" /></F>
        </Two>
        {a.incomeMinor > 0 && (
          <p className={`rounded-[12px] border px-4 py-3 text-[14px] ${a.affordable ? 'border-bronze-200 bg-bronze-50/50 text-ink-700' : 'border-amber-300 bg-amber-50/60 text-amber-800'}`}>
            {a.affordable ? `Covers rent up to ${formatRent(a.maxRentMinor, 'GBP')} a month — this home is within it.` : `Covers rent up to ${formatRent(a.maxRentMinor, 'GBP')} a month, so you'll need a guarantor.`}
          </p>
        )}
      </Group>

      <Group>
        <Tick checked={!s.firstTimeRenter} onChange={(rented) => set({ firstTimeRenter: !rented })}>I have rented before — add my previous landlord or agent</Tick>
        {!s.firstTimeRenter && <LandlordFields s={s} set={set} need={asked ? need : undefined} />}
      </Group>

      <Group>
        <RightToRent s={s} set={set} required={need('nationality')} askedShareCode={!!asked && need('shareCode')} />
      </Group>

      {/* Every tick box, at the bottom: the optional ones first, then the three the application cannot go without. */}
      <Group>
        <div className="grid gap-x-6 sm:grid-cols-2">
          <Tick checked={s.pets} onChange={(pets) => set({ pets })}>We have pets</Tick>
          <Tick checked={s.smoker} onChange={(smoker) => set({ smoker })}>Someone moving in smokes</Tick>
          <Tick checked={s.guarantor} onChange={(guarantor) => set({ guarantor })}>I have a guarantor</Tick>
          {!s.firstTimeRenter && <Tick checked={s.arrears} onChange={(arrears) => set({ arrears })}>There were rent arrears</Tick>}
          <Tick checked={s.parkingRequired} onChange={(parkingRequired) => set({ parkingRequired })}>Parking required</Tick>
          <Tick checked={s.furnitureRequired} onChange={(furnitureRequired) => set({ furnitureRequired })}>Furniture required <span className="text-ink-400">(extra cost)</span></Tick>
        </div>
        {(s.pets || s.guarantor) && <Two>
          {s.pets && <F label="What pets?" name="petsDetail" required={!!asked && need('pets')}><input value={s.petsDetail} onChange={(e) => set({ petsDetail: e.target.value })} className="site-input" /></F>}
          {s.guarantor && <GuarantorFields s={s} set={set} />}
        </Two>}
      </Group>

      {consents && (
        <div className={`mt-8 rounded-[14px] border px-4 py-2 sm:px-5 sm:py-3 ${show('consent') ? 'border-red-300 bg-red-50/40' : 'border-bronze-200 bg-bronze-50/50'}`} data-invalid={show('consent') ? '' : undefined}>
          <Tick required checked={s.privacy} onChange={(privacy) => set({ privacy })}>I have read the privacy notice.</Tick>
          <Tick required checked={s.referencing} onChange={(referencing) => set({ referencing })}>I consent to referencing and a credit check, and to Lotus Living contacting my employer, previous landlord and guarantor.</Tick>
          <Tick required checked={s.declaration} onChange={(declaration) => set({ declaration })}>What I have told you is true and complete, and this is my signature{nameOf(s) ? ` as ${nameOf(s)}` : ''}.</Tick>
          {show('consent') && <p className="site-error pb-2">{show('consent')}</p>}
        </div>
      )}
    </>
  );
}

// ── The website's own two forms in four steps: Request a viewing and Apply ────────────────────────────────────────
// About you → Where you've lived (with Right to Rent and the documents) → Money (with the previous landlord) → The move
// (with when suits you, the optional ticks and the three consents). Next stays off until every field on the step is
// right (the same rules, siteFormErrors filtered to the step); Back keeps everything typed; a step already done can be
// opened again from the indicator. A refused submit goes to the step that holds the field and says so under it.

type StepsProps = {
  s: ApplicationState; set: (p: Partial<ApplicationState>) => void; advert: number; mode?: FormMode;
  docs: FormDocs; setDocs: (d: FormDocs) => void;
  /** The last step's button: "Request a viewing", "Submit application". */
  submitLabel: string;
  /** Sending: the button says so and stays off. */
  pending: boolean;
  /** Every field is right: send it. */
  onSubmit: () => void;
  /** The last refused submit (the mutation's error): read as a field (serverFieldError) and shown under it. */
  error?: unknown;
  /** A line above the buttons on every step (a refusal that is about no field, or one that needs a link). */
  notice?: ReactNode;
  /** The small print under the buttons. */
  footnote?: string;
};

export function SiteApplicationSteps({ s, set, advert, mode = 'apply', docs, setDocs, submitLabel, pending, onSubmit, error, notice, footnote }: StepsProps) {
  const errs = siteFormErrors(s, advert, { mode, docs });
  const st = useFormSteps(errs, { error, onSubmit });
  // Editing clears the server's word on the field — the field's own rules take over again.
  const edit = (p: Partial<ApplicationState>) => { st.edited(); set(p); };
  const editDocs = (d: FormDocs) => { st.edited(); setDocs(d); };
  const { step } = st;

  return (
    <form ref={st.top as RefObject<HTMLFormElement | null>} onSubmit={st.submit} noValidate className="mt-6 scroll-mt-28 sm:mt-8">
      <StepIndicator step={step} />
      <ErrCtx.Provider value={{ show: st.show, touch: st.touch, docs, setDocs: editDocs }}>
        <div className="mt-7 sm:mt-8">
          {step === 0 && <StepAboutYou s={s} set={edit} />}
          {step === 1 && <StepLived s={s} set={edit} />}
          {step === 2 && <StepMoney s={s} set={edit} advert={advert} />}
          {step === 3 && <StepMove s={s} set={edit} advert={advert} mode={mode} />}
        </div>
      </ErrCtx.Provider>

      <div className="mt-8 space-y-3 border-t border-ink-100 pt-6">
        {notice}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          {step > 0
            ? <button type="button" onClick={st.back} className="inline-flex min-h-[48px] items-center justify-center gap-1.5 text-[14px] font-medium text-ink-600 transition hover:text-ink-900"><ArrowLeft className="h-4 w-4" /> Back</button>
            : <span className="hidden sm:block" />}
          <PillButton type="submit" disabled={!st.stepOk || pending} className="w-full disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto">
            {st.last ? (pending ? 'Sending…' : submitLabel) : 'Next'}
          </PillButton>
        </div>
        {footnote && st.last && <p className="text-[12.5px] text-ink-400">{footnote}</p>}
      </div>
    </form>
  );
}

/** "Step 2 of 4" — nothing else (no names, no bar). */
function StepIndicator({ step }: { step: number }) {
  return (
    <nav aria-label="Form steps">
      <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-ink-500" aria-live="polite">
        Step {step + 1} of {FORM_STEPS.length}
      </p>
    </nav>
  );
}

type StepFieldsProps = { s: ApplicationState; set: (p: Partial<ApplicationState>) => void };

function StepAboutYou({ s, set }: StepFieldsProps) {
  return (
    <section className="space-y-4">
      <Heading>About you</Heading>
      <Two>
        <F label="First name" required name="firstName"><input required autoComplete="given-name" value={s.firstName} onChange={(e) => set({ firstName: e.target.value })} className="site-input" /></F>
        <F label="Last name" required name="lastName"><input required autoComplete="family-name" value={s.lastName} onChange={(e) => set({ lastName: e.target.value })} className="site-input" /></F>
        <F label="Date of birth" required name="dateOfBirth"><input required type="date" autoComplete="bday" max={todayIso()} value={s.dateOfBirth} onChange={(e) => set({ dateOfBirth: e.target.value })} className="site-input" /></F>
        <F label="Email" required name="email"><input required type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} value={s.email} onChange={(e) => set({ email: e.target.value })} className="site-input" /></F>
        <F label="Mobile" required name="mobile"><PhoneInput kit="site" required value={s.mobile} onChange={(mobile) => set({ mobile })} /></F>
      </Two>
      <Members s={s} set={set} />
    </section>
  );
}

function StepLived({ s, set }: StepFieldsProps) {
  const { show } = useContext(ErrCtx);
  const covered = monthsCovered(s);
  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <Heading hint="The last 3 years">Where you have lived</Heading>
        <AddressRow a={s.current} onChange={(current) => set({ current })} current required />
        {s.previous.map((p, i) => <AddressRow key={i} a={p} onChange={(x) => set({ previous: s.previous.map((y, j) => (j === i ? x : y)) })} onRemove={() => set({ previous: s.previous.filter((_, j) => j !== i) })} />)}
        <div className="flex flex-wrap items-center justify-between gap-x-4">
          <p className={`text-[13px] ${show('addressHistory') ? 'text-[var(--status-critical)]' : covered >= ADDRESS_HISTORY_MONTHS ? 'text-bronze-700' : 'text-ink-500'}`} data-invalid={show('addressHistory') ? '' : undefined}>{Math.floor(covered / 12)}y {covered % 12}m of the 3 years needed{show('addressHistory') ? ` — ${show('addressHistory')}` : ''}</p>
          <AddLink onClick={() => set({ previous: [...s.previous, emptyAddress()] })}>Previous address</AddLink>
        </div>
      </section>
      <section className="space-y-4">
        <Heading>Right to Rent</Heading>
        <RightToRent s={s} set={set} required />
      </section>
    </div>
  );
}

function StepMoney({ s, set, advert }: StepFieldsProps & { advert: number }) {
  const incomeNeeded = s.employmentStatus !== 'STUDENT' && s.employmentStatus !== 'RETIRED';
  const a = affordability(s, minorOf(s.offeredRent) || advert);
  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <Heading>Money</Heading>
        <Two>
          <F label="Employment" required><select required value={s.employmentStatus} onChange={(e) => set({ employmentStatus: e.target.value })} className="site-select">{EMPLOYMENT_STATUSES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}</select></F>
          <F label="Employer" name="employer"><input autoComplete="organization" value={s.employer} onChange={(e) => set({ employer: e.target.value })} className="site-input" /></F>
          <F label="Job title" name="jobTitle"><input autoComplete="organization-title" value={s.jobTitle} onChange={(e) => set({ jobTitle: e.target.value })} className="site-input" /></F>
          <F label="Start date" name="startDate"><input type="date" value={s.startDate} onChange={(e) => set({ startDate: e.target.value })} className="site-input" /></F>
          <F label="Annual income before tax (£)" required={incomeNeeded} name="annualIncome"><input required={incomeNeeded} inputMode="decimal" value={s.annualIncome} onChange={(e) => set({ annualIncome: e.target.value })} className="site-input" /></F>
          <F label="Other income (£)" name="otherIncome"><input inputMode="decimal" value={s.otherIncome} onChange={(e) => set({ otherIncome: e.target.value })} placeholder="Including benefits" className="site-input" /></F>
        </Two>
        {a.incomeMinor > 0 && (
          <p className={`rounded-[12px] border px-4 py-3 text-[14px] ${a.affordable ? 'border-bronze-200 bg-bronze-50/50 text-ink-700' : 'border-amber-300 bg-amber-50/60 text-amber-800'}`}>
            {a.affordable ? `Covers rent up to ${formatRent(a.maxRentMinor, 'GBP')} a month — this home is within it.` : `Covers rent up to ${formatRent(a.maxRentMinor, 'GBP')} a month, so you'll need a guarantor.`}
          </p>
        )}
        <Tick checked={s.guarantor} onChange={(guarantor) => set({ guarantor })}>I have a guarantor</Tick>
        {s.guarantor && <Two><GuarantorFields s={s} set={set} /></Two>}
      </section>
      <section className="space-y-4">
        <Heading>Previous landlord</Heading>
        <Tick checked={!s.firstTimeRenter} onChange={(rented) => set({ firstTimeRenter: !rented })}>I have rented before — add my previous landlord or agent</Tick>
        {!s.firstTimeRenter && <LandlordFields s={s} set={set} />}
        {!s.firstTimeRenter && <Tick checked={s.arrears} onChange={(arrears) => set({ arrears })}>There were rent arrears</Tick>}
      </section>
    </div>
  );
}

function StepMove({ s, set, advert, mode }: StepFieldsProps & { advert: number; mode: FormMode }) {
  const { show, touch } = useContext(ErrCtx);
  const slots = s.preferredSlots ?? [];
  const setSlot = (i: number, patch: Partial<{ date: string; time: string }>) => set({ preferredSlots: slots.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <Heading>The move</Heading>
        <Two>
          <F label="Move-in date" required name="moveInDate" hint={freeFromLine(s, 'Available')}><input required type="date" min={earliestMoveIn(s)} value={s.moveInDate} onChange={(e) => set({ moveInDate: e.target.value })} className="site-input" /></F>
          <F label="Rent you offer a month (£)" required name="offeredRent"><input required inputMode="decimal" placeholder={advert ? String(advert / 100) : ''} min={advert ? minUnderAdvertMinor(advert) / 100 : undefined} max={advert ? advert / 100 : undefined} value={s.offeredRent} onChange={(e) => set({ offeredRent: e.target.value })} className="site-input" /></F>
        </Two>
      </section>

      {mode === 'viewing' && (
        <section className="space-y-3" onBlur={() => touch('preferredSlots')} data-invalid={show('preferredSlots') ? '' : undefined}>
          <Heading hint="Weekdays, up to three">When suits you <span className="text-bronze-500">*</span></Heading>
          <div className="space-y-2">
            {slots.map((sl, i) => (
              // Phones: day on top, hour under it; from 640px the two sit side by side.
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_1.75rem] items-center gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,8.75rem)_1.75rem]">
                <input type="date" min={todayIso()} value={sl.date} onChange={(e) => setSlot(i, { date: e.target.value })} aria-invalid={sl.date && !isWeekday(sl.date) ? true : undefined} className="site-input col-span-2 px-3 sm:col-span-1" aria-label={`Day ${i + 1}`} />
                <select value={sl.time} onChange={(e) => setSlot(i, { time: e.target.value })} className={`site-select pl-3 pr-8 [background-position:right_0.5rem_center] sm:col-span-1 sm:col-start-2 sm:row-start-1 ${i > 0 ? '' : 'col-span-2'}`} aria-label={`Hour ${i + 1}`}>
                  {VIEWING_HOURS.map((h) => <option key={h} value={h}>{slotLabel(h)}</option>)}
                </select>
                {i > 0 ? <button type="button" onClick={() => set({ preferredSlots: slots.filter((_, j) => j !== i) })} className="col-start-2 row-start-2 grid h-11 w-7 place-items-center text-ink-400 hover:text-ink-900 sm:col-start-3 sm:row-start-1" aria-label="Remove this time"><X className="h-4 w-4" /></button> : null}
              </div>
            ))}
          </div>
          {slots.length < 3 && <button type="button" onClick={() => { set({ preferredSlots: [...slots, { date: nextWeekday(slots.length + 1), time: '14:00' }] }); touch('preferredSlots'); }} className="inline-flex min-h-[40px] items-center gap-1.5 text-[13px] font-medium text-bronze-600 hover:text-ink-900"><Plus className="h-3.5 w-3.5" />{slots.length ? 'Add another time' : 'Add a time'}</button>}
          {show('preferredSlots') && <p className="site-error">{show('preferredSlots')}</p>}
        </section>
      )}

      <section>
        <Heading>Anything else</Heading>
        <div className="mt-2 grid">
          <Tick checked={s.pets} onChange={(pets) => set({ pets })}>We have pets</Tick>
          {s.pets && <F label="What pets?" name="petsDetail"><input value={s.petsDetail} onChange={(e) => set({ petsDetail: e.target.value })} className="site-input" /></F>}
          <Tick checked={s.smoker} onChange={(smoker) => set({ smoker })}>Someone moving in smokes</Tick>
          <Tick checked={s.parkingRequired} onChange={(parkingRequired) => set({ parkingRequired })}>Parking required</Tick>
          <Tick checked={s.furnitureRequired} onChange={(furnitureRequired) => set({ furnitureRequired })}>Furniture required <span className="text-ink-400">(extra cost)</span></Tick>
        </div>
      </section>

      {/* The three consents the form cannot go without; in a viewing request the first one is also to arrange the viewing. */}
      <div className={`rounded-[14px] border px-4 py-2 ${show('consent') ? 'border-red-300 bg-red-50/40' : 'border-bronze-200 bg-bronze-50/50'}`} data-invalid={show('consent') ? '' : undefined} onChange={() => touch('consent')}>
        <Tick required checked={s.privacy} onChange={(privacy) => set({ privacy })}>I have read the privacy notice{mode === 'viewing' ? ', and you may contact me about this home to arrange the viewing' : ''}.</Tick>
        <Tick required checked={s.referencing} onChange={(referencing) => set({ referencing })}>I consent to referencing and a credit check, and to Lotus Living contacting my employer, previous landlord and guarantor.</Tick>
        <Tick required checked={s.declaration} onChange={(declaration) => set({ declaration })}>What I have told you is true and complete, and this is my signature{nameOf(s) ? ` as ${nameOf(s)}` : ''}.</Tick>
        {show('consent') && <p className="site-error pb-2">{show('consent')}</p>}
      </div>
    </div>
  );
}

// ── Shared pieces ─────────────────────────────────────────────────────────────────────────────────────────────────

function Members({ s, set }: { s: ApplicationState; set: (p: Partial<ApplicationState>) => void }) {
  return (
    <>
      {s.members.map((m, i) => {
        const upd = (patch: Partial<typeof m>) => set({ members: s.members.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
        return (
          <div key={i} className="rounded-[14px] border border-ink-200 bg-ink-50/40 p-4 sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <span className="text-[14px] font-medium text-ink-900">Person {i + 2}</span>
              <RemoveBtn onClick={() => set({ members: s.members.filter((_, j) => j !== i) })} label={`Remove person ${i + 2}`} />
            </div>
            <Two>
              <F label="Full name" required name={`member.${i}`}><input required value={m.name} onChange={(e) => upd({ name: e.target.value })} className="site-input" /></F>
              <F label="Date of birth"><input type="date" value={m.dateOfBirth} onChange={(e) => upd({ dateOfBirth: e.target.value })} className="site-input" /></F>
              <F label="Relationship"><input value={m.relationship} onChange={(e) => upd({ relationship: e.target.value })} placeholder="e.g. partner" className="site-input" /></F>
              <label className="site-check self-end"><input type="checkbox" checked={m.adult} onChange={(e) => upd({ adult: e.target.checked })} />Adult, named on the tenancy</label>
            </Two>
          </div>
        );
      })}
      <AddLink onClick={() => set({ members: [...s.members, { name: '', dateOfBirth: '', relationship: '', adult: true }] })}>Add a person moving in</AddLink>
    </>
  );
}
/**
 * Nationality from the list (lib/nationalities.ts) and, for anyone who is not British or Irish, the Right to Rent share
 * code — required then, here and on the server (applicant.schemas.ts). British and Irish: no share code, so no box.
 * Answering a refill that asks for the share code shows it whatever the nationality.
 */
function RightToRent({ s, set, required, askedShareCode }: { s: ApplicationState; set: (p: Partial<ApplicationState>) => void; required?: boolean; askedShareCode?: boolean }) {
  const needs = needsShareCode(s.nationality);
  const { show, docs, setDocs } = useContext(ErrCtx);
  return (
    <Two>
      <F label="Nationality" required={required} name="nationality"><NationalitySelect kit="site" required={required} invalid={!!show('nationality')} value={s.nationality} onChange={(nationality) => set({ nationality })} /></F>
      {(needs || askedShareCode) && (
        <F label="Right to Rent share code" required name="shareCode">
          <input required autoCapitalize="characters" autoComplete="off" spellCheck={false} value={s.shareCode} onChange={(e) => set({ shareCode: e.target.value.toUpperCase() })} placeholder="e.g. W2X 7K9 Q4L" className="site-input" />
          {needs && !show('shareCode') && <span className="site-hint">Needed unless you are British or Irish. Get yours at <a href="https://www.gov.uk/prove-right-to-rent" target="_blank" rel="noreferrer" className="underline underline-offset-2">gov.uk/prove-right-to-rent</a>.</span>}
        </F>
      )}
      {/* The documents, once a nationality is picked: the passport for everyone, the right to rent document unless British or Irish. */}
      {docs && setDocs && findNationality(s.nationality) && <>
        <DocBox name="passport" label="Passport" hint="The photo page" value={docs.id ?? null} onChange={(id) => setDocs({ ...docs, id })} />
        {needsRtrDocument(s.nationality) && <DocBox name="rtrDocument" label="Right to rent document" hint="Your share-code check result, BRP or visa page" value={docs.rtr ?? null} onChange={(rtr) => setDocs({ ...docs, rtr })} />}
      </>}
    </Two>
  );
}

/** One required document: tap (or drop a file) to pick a photo or PDF up to 7 MB; the chosen file's name, with Remove. */
function DocBox({ name, label, hint, value, onChange }: { name: string; label: string; hint: string; value: Upload | null; onChange: (u: Upload | null) => void }) {
  const { show, touch } = useContext(ErrCtx);
  const [local, setLocal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const err = local ?? show(name);
  const pick = async (file: File | undefined) => {
    touch(name);
    if (!file) return;
    const bad = docFileError(file);
    if (bad) { setLocal(bad); return; }
    setLocal(null); setBusy(true);
    try { onChange(await fileToUpload(file)); } catch { setLocal('Could not read that file — try another'); } finally { setBusy(false); }
  };
  return (
    <div className="site-field" data-invalid={err ? '' : undefined}>
      <span className="site-label">{label}<span className="text-bronze-500"> *</span></span>
      {value ? (
        <div className="flex min-h-[48px] items-center gap-2.5 rounded-[12px] border border-bronze-200 bg-bronze-50/40 px-3.5 py-2 text-[14px]">
          <FileText className="h-4 w-4 shrink-0 text-bronze-600" />
          <span className="min-w-0 flex-1 truncate text-ink-800">{value.fileName}</span>
          <button type="button" onClick={() => { onChange(null); setLocal(null); }} className="shrink-0 text-[13px] font-medium text-bronze-700 underline-offset-2 hover:underline">Remove</button>
        </div>
      ) : (
        <label
          className={`flex min-h-[48px] cursor-pointer items-center gap-3 rounded-[12px] border border-dashed px-3.5 py-2 transition ${over ? 'border-bronze-500 bg-bronze-50' : err ? 'border-red-300 bg-red-50/30' : 'border-ink-300 bg-white hover:border-bronze-400 hover:bg-bronze-50/40'} ${busy ? 'pointer-events-none opacity-60' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); void pick(e.dataTransfer.files?.[0]); }}
        >
          <UploadIcon className="h-4 w-4 shrink-0 text-bronze-600" />
          <span className="min-w-0 leading-snug">
            <span className="block text-[14px] font-medium text-ink-800">{busy ? 'Reading…' : 'Upload a photo or PDF'}</span>
            <span className="block text-[12.5px] text-ink-500">{hint} · up to 7 MB</span>
          </span>
          <input type="file" accept="application/pdf,image/*" className="sr-only" disabled={busy} onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
      )}
      {err && <span className="site-error">{err}</span>}
    </div>
  );
}
function LandlordFields({ s, set, need }: { s: ApplicationState; set: (p: Partial<ApplicationState>) => void; need?: (k: string) => boolean }) {
  return (
    <Two>
      <F label="Previous landlord or agent" name="landlordName" required={need?.('landlordName')}><input value={s.landlordName} onChange={(e) => set({ landlordName: e.target.value })} className="site-input" /></F>
      <F label="Their email" name="landlordEmail" required={need?.('landlordEmail')}><input type="email" inputMode="email" autoCapitalize="none" spellCheck={false} value={s.landlordEmail} onChange={(e) => set({ landlordEmail: e.target.value })} className="site-input" /></F>
      <F label="Their phone" name="landlordPhone" required={need?.('landlordPhone')}><PhoneInput kit="site" autoComplete="off" value={s.landlordPhone} onChange={(landlordPhone) => set({ landlordPhone })} /></F>
      <F label="How long you rented"><input value={s.landlordHowLong} onChange={(e) => set({ landlordHowLong: e.target.value })} placeholder="e.g. 2 years" className="site-input" /></F>
    </Two>
  );
}
function GuarantorFields({ s, set }: { s: ApplicationState; set: (p: Partial<ApplicationState>) => void }) {
  return (
    <>
      <F label="Guarantor's name" required name="guarantorName"><input required value={s.guarantorName} onChange={(e) => set({ guarantorName: e.target.value })} className="site-input" /></F>
      <F label="Guarantor's email" required name="guarantorEmail"><input required type="email" inputMode="email" autoCapitalize="none" spellCheck={false} value={s.guarantorEmail} onChange={(e) => set({ guarantorEmail: e.target.value })} className="site-input" /></F>
    </>
  );
}

// A group of fields: a hairline between groups, no headings. Inside it, rows are two equal columns from 640px, one on a phone.
const Group = ({ children, first }: { children: ReactNode; first?: boolean }) => <div className={`space-y-4 ${first ? 'mt-6 sm:mt-8' : 'mt-7 border-t border-ink-100 pt-7'}`}>{children}</div>;
// Two columns once the form itself is wide enough (a container query, so a phone field with its country code keeps the
// whole row on a phone).
const Two = ({ children }: { children: ReactNode }) => <div className="@container"><div className="grid gap-4 @[30rem]:grid-cols-2 @[30rem]:gap-x-5">{children}</div></div>;
const Heading = ({ children, hint }: { children: ReactNode; hint?: string }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-ink-100 pb-2"><h3 className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-700">{children}</h3>{hint && <span className="text-[12px] text-ink-400">{hint}</span>}</div>
);
/** Per-field problems, from the form: what to show for a field, and that the person has been in it. */
const ErrCtx = createContext<{ show: (k: string) => string | undefined; touch: (k: string) => void; docs?: FormDocs; setDocs?: (d: FormDocs) => void }>({ show: () => undefined, touch: () => {} });
const F = ({ label, required, name, hint, children }: { label: string; required?: boolean; name?: string; hint?: string | null; children: ReactNode }) => {
  const { show, touch } = useContext(ErrCtx);
  const err = name ? show(name) : undefined;
  return (
    <label className="site-field" onBlur={() => name && touch(name)} data-invalid={err ? '' : undefined}>
      <span className="site-label">{label}{required && <span className="text-bronze-500"> *</span>}</span>
      {children}
      {err ? <span className="site-error">{err}</span> : hint ? <span className="text-[12.5px] text-ink-500">{hint}</span> : null}
    </label>
  );
};
/** A tick box; a required one is the applicant's consent and reads bolder, with the star. */
const Tick = ({ checked, required, onChange, children }: { checked: boolean; required?: boolean; onChange: (v: boolean) => void; children: ReactNode }) => (
  <label className={`site-check ${required ? '!text-[14px] font-medium !text-ink-900' : ''}`}>
    <input required={required} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span>{children}{required && <span className="text-bronze-500"> *</span>}</span>
  </label>
);
const AddLink = ({ onClick, children }: { onClick: () => void; children: ReactNode }) => (
  <button type="button" onClick={onClick} className="inline-flex min-h-[44px] items-center gap-1.5 text-[14px] font-medium text-bronze-600 transition hover:text-ink-900"><Plus className="h-4 w-4" /> {children}</button>
);
const RemoveBtn = ({ onClick, label, hidden }: { onClick?: () => void; label: string; hidden?: boolean }) => (
  <button type="button" onClick={onClick} disabled={hidden} className={`grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink-400 transition hover:bg-ink-100 hover:text-ink-900 ${hidden ? 'invisible' : ''}`} aria-label={label}><X className="h-4 w-4" /></button>
);
/** An address on the left; how long they were there (years | months) fills the right half, so it lines up with every other row. */
function AddressRow({ a, onChange, onRemove, current, required }: { a: AddressLine; onChange: (a: AddressLine) => void; onRemove?: () => void; current?: boolean; required?: boolean }) {
  return (
    <Two>
      <F label={current ? 'Current address' : 'Previous address'} required={required} name={current ? 'currentAddress' : undefined}><input required={required} autoComplete={current ? 'street-address' : 'off'} value={a.address} onChange={(e) => onChange({ ...a, address: e.target.value })} className="site-input" /></F>
      {/* The remove cross keeps its space on every row, so the boxes line up. */}
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem] items-end gap-3">
        <F label="Years there" required={required}><input required={required} inputMode="numeric" value={a.years} onChange={(e) => onChange({ ...a, years: e.target.value.replace(/\D/g, '') })} className="site-input" /></F>
        <F label="Months"><input inputMode="numeric" value={a.months} onChange={(e) => onChange({ ...a, months: e.target.value.replace(/\D/g, '') })} className="site-input" /></F>
        <RemoveBtn onClick={onRemove} hidden={!onRemove} label="Remove address" />
      </div>
    </Two>
  );
}

// ── My application: what the applicant sent, read-only, in the site's styling (/apply/<token>) ─────────────────────────

type J = Record<string, any>;
const day = (v?: string | null) => (v ? new Date(`${v.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : null);
const span = (m: unknown) => { const n = Number(m) || 0; return n ? [Math.floor(n / 12) && `${Math.floor(n / 12)}y`, n % 12 && `${n % 12}m`].filter(Boolean).join(' ') : null; };

/** The form as last saved (reservations.application), section by section; "—" where nothing was given. Folded to start. */
export function SiteApplicationSummary({ form, open: startOpen = false }: { form: J | null | undefined; open?: boolean }) {
  const [open, setOpen] = useState(startOpen);
  if (!form || typeof form !== 'object') return null;
  const you: J = form.aboutYou ?? {}; const hh: J = form.household ?? {}; const mv: J = form.move ?? {};
  const mo: J | null = form.money ?? null; const pl: J | null = form.previousLandlord ?? null; const rtr: J | null = form.rightToRent ?? null;
  const members: J[] = Array.isArray(hh.members) ? hh.members : [];
  const addresses: J[] = [you.currentAddress, ...(Array.isArray(you.previousAddresses) ? you.previousAddresses : [])].filter((a: J | undefined) => a?.address);
  const money = (m: unknown) => (typeof m === 'number' && m > 0 ? pounds(m) : null);
  const sections: Array<[string, Array<[string, ReactNode]>]> = [
    ['About you', [['Name', [you.title, you.fullName].filter(Boolean).join(' ')], ['Date of birth', day(you.dateOfBirth)], ['Email', you.email], ['Mobile', formatPhone(you.mobile)]]],
    ['Where you have lived', addresses.length ? addresses.map((a, i) => [i === 0 && you.currentAddress?.address ? 'Now' : 'Before', [a.address, span(a.months)].filter(Boolean).join(' · ')]) : [['Addresses', null]]],
    ['Who is moving in', [
      ['People', String(1 + members.length)],
      ...members.map((m, i): [string, ReactNode] => [`Person ${i + 2}`, [m.name, m.relationship, m.adult === false ? 'child' : null].filter(Boolean).join(' · ')]),
      ['Pets', hh.pets ? (hh.pets.has ? hh.pets.detail || 'Yes' : 'No') : null], ['Smoker', hh.smoker == null ? null : hh.smoker ? 'Yes' : 'No'],
    ]],
    ['The move', [['Move-in date', day(mv.moveInDate)], ['Rent offered a month', money(mv.offeredRentMinor)], ...(mv.offerReason ? [['Why less', mv.offerReason] as [string, ReactNode]] : []),
      ['Parking', mv.parkingRequired ? 'Required' : 'Not needed'], ['Furniture', mv.furnitureRequired ? 'Required' : 'Not needed']]],
    ['Money', [['Employment', EMPLOYMENT_STATUSES.find((e) => e.key === mo?.employmentStatus)?.label ?? null], ['Employer', mo?.employer], ['Job title', mo?.jobTitle],
      ['Annual income', money(mo?.annualIncomeMinor)], ['Other income', money(mo?.otherIncomeMinor)], ['Guarantor', mo?.guarantor?.has ? [mo.guarantor.name, mo.guarantor.email].filter(Boolean).join(' · ') : mo ? 'No' : null]]],
    ['Previous landlord', pl?.firstTimeRenter ? [['', 'First time renting']] : [['Name', pl?.name], ['Email', pl?.email], ['Phone', formatPhone(pl?.phone)], ['How long', pl?.howLong]]],
    ['Right to Rent', [['Nationality', rtr?.nationality], ['Share code', rtr?.shareCode]]],
  ];
  return (
    <div className="rounded-[14px] border border-ink-200 bg-white">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex min-h-[52px] w-full items-center justify-between gap-3 px-4 text-left sm:px-5">
        <span><span className="block text-[16px] font-medium text-ink-900">My application</span><span className="block text-[13px] text-ink-500">What you sent Lotus Living</span></span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-ink-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-5 border-t border-ink-100 px-4 py-4 sm:px-5">
          {sections.map(([title, rows]) => (
            <div key={title}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-400">{title}</p>
              <dl className="mt-2 grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[14px]">
                {rows.map(([k, v], i) => <div key={`${k}${i}`} className="contents"><dt className="text-ink-500">{k}</dt><dd className="min-w-0 break-words text-ink-900">{v || <span className="text-ink-300">—</span>}</dd></div>)}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
